import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, ArrowDownLeft, Check, Loader2, Send, CreditCard } from 'lucide-react';
import { expensesAPI } from '@/services/api';
import { useAuth } from '@/context/AuthContext';

const ExpenseDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  
  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchExpense();
  }, [id]);

  const fetchExpense = async () => {
    try {
      setLoading(true);
      const response = await expensesAPI.get(id);
      setExpense(response.data);
    } catch (err) {
      setError('Expense not found');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const num = Math.abs(amount);
    if (num === Math.floor(num)) {
      return `₹${num.toLocaleString('en-IN')}`;
    }
    return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (error || !expense) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <p className="text-lg font-medium mb-4">{error || 'Expense not found'}</p>
        <button onClick={() => navigate('/home')} className="btn-secondary">
          Go Home
        </button>
      </div>
    );
  }

  const isPayer = expense.paid_by_user_id === user?.id;
  const split = expense.splits?.[0];
  const isSettled = expense.status === 'settled';

  // Determine friend info
  const friendName = isPayer 
    ? (split?.user_name || split?.owed_by_phone || 'Friend')
    : (expense.paid_by_name || expense.paid_by_phone || 'Friend');

  const amountOwed = split?.amount_owed || 0;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="sticky top-0 bg-white z-10 px-6 py-4 flex items-center gap-4 border-b-2 border-black">
        <button 
          onClick={() => navigate('/home')}
          className="hover:opacity-70 transition-opacity"
          data-testid="back-button"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-xl font-heading font-bold">Expense Details</h1>
      </header>

      <main className="flex-1 p-6 pb-32 space-y-6">
        {/* Amount & Description Card */}
        <div className="card-base text-center">
          <p className="text-5xl font-heading font-bold mb-2">
            {formatCurrency(expense.total_amount)}
          </p>
          <p className="text-lg text-neutral-600">
            {expense.description || 'Split'}
          </p>
          <p className="text-sm text-neutral-400 mt-2">
            {formatDate(expense.created_at)}
          </p>
        </div>

        {/* Split Details */}
        <div className="card-base space-y-4">
          <h2 className="font-heading font-bold text-lg">Split Details</h2>
          
          {/* Who paid */}
          <div className="flex items-center gap-4 pb-4 border-b-2 border-dashed border-neutral-200">
            <div className="w-12 h-12 rounded-full bg-black text-white flex items-center justify-center font-bold">
              {isPayer ? (user?.name?.[0] || 'Y') : (expense.paid_by_name?.[0] || 'F')}
            </div>
            <div className="flex-1">
              <p className="font-bold">{isPayer ? 'You' : friendName}</p>
              <p className="text-sm text-neutral-500">paid {formatCurrency(expense.total_amount)}</p>
            </div>
          </div>

          {/* Who owes */}
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              isPayer ? 'bg-brand-purple text-white' : 'bg-money-out text-white'
            }`}>
              {isPayer 
                ? (split?.user_name?.[0] || split?.owed_by_phone?.[0] || 'F')
                : (user?.name?.[0] || 'Y')
              }
            </div>
            <div className="flex-1">
              <p className="font-bold">{isPayer ? friendName : 'You'}</p>
              <p className={`text-sm ${isPayer ? 'text-money-in' : 'text-money-out'}`}>
                {isPayer ? 'owes you' : 'owe'} {formatCurrency(amountOwed)}
              </p>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-bold ${
              isSettled 
                ? 'bg-green-100 text-green-700' 
                : 'bg-yellow-100 text-yellow-700'
            }`}>
              {isSettled ? 'Settled' : 'Pending'}
            </span>
          </div>

          {/* Settlement info */}
          {isSettled && split?.settled_at && (
            <div className="pt-4 border-t-2 border-dashed border-neutral-200 text-center">
              <p className="text-sm text-neutral-500">
                Settled on {formatDate(split.settled_at)}
              </p>
              <p className="text-xs text-neutral-400">
                Confirmed by {split.settled_by === 'payer' ? 'payer' : 'payee'}
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Fixed Bottom Actions */}
      {!isSettled && (
        <div className="fixed bottom-0 left-0 right-0 p-6 bg-white border-t-2 border-black space-y-3">
          {isPayer ? (
            <>
              <button
                onClick={() => navigate(`/expenses/${id}/request`)}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="request-payment-button"
              >
                <Send className="w-5 h-5" />
                Request Payment
              </button>
              <button
                onClick={() => navigate(`/expenses/${id}/confirm`)}
                className="btn-secondary w-full flex items-center justify-center gap-2"
                data-testid="mark-received-button"
              >
                <Check className="w-5 h-5" />
                Mark as Received
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  // Generate UPI link and open it
                  expensesAPI.getUPILink(id).then(res => {
                    window.open(res.data.upi_link, '_blank');
                  });
                }}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="pay-now-button"
              >
                <CreditCard className="w-5 h-5" />
                Pay Now (UPI)
              </button>
              <button
                onClick={() => navigate(`/expenses/${id}/confirm`)}
                className="btn-secondary w-full flex items-center justify-center gap-2"
                data-testid="i-have-paid-button"
              >
                <Check className="w-5 h-5" />
                I Have Paid
              </button>
            </>
          )}
        </div>
      )}

      {/* Settled state */}
      {isSettled && (
        <div className="fixed bottom-0 left-0 right-0 p-6 bg-white border-t-2 border-black">
          <div className="card-base text-center bg-green-50 border-green-500">
            <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="font-bold text-green-700">Settled</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseDetailPage;
